import { create } from "zustand";

import {
  AUTH_CONSTANTS,
  LOCAL_STORAGE_KEYS,
  SESSION_STORAGE_KEYS,
} from "@/shared/config";
import { getMeQueryKey, getMeQueryOptions } from "@/shared/api/generated-kubb/hooks/useGetMe";
import { getQueryClient } from "@/shared/query-client";

const FRESH_MS = 5 * 60 * 1000;
const AUTH_HINT_TTL_MS = 5 * 60 * 1000;
let authGeneration = 0;

function getAuthHint(): { userId: string; ts: number } | null {
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEYS.AUTH_HINT);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { userId?: string; ts?: number };
    if (
      typeof parsed?.userId !== "string" ||
      typeof parsed?.ts !== "number" ||
      Date.now() - parsed.ts > AUTH_HINT_TTL_MS
    )
      return null;
    return { userId: parsed.userId, ts: parsed.ts };
  } catch {
    return null;
  }
}

function setAuthHint(userId: string) {
  try {
    sessionStorage.setItem(
      SESSION_STORAGE_KEYS.AUTH_HINT,
      JSON.stringify({ userId, ts: Date.now() }),
    );
  } catch {
    // ignore
  }
}

export function clearAuthHint() {
  try {
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.AUTH_HINT);
  } catch {
    // ignore
  }
}

const setAuthFromCache = (cached: unknown, persistHint = false) => {
  const user = (cached as { data?: { id?: string; username?: string } })?.data ?? null;
  const isValidUser =
    !!user && (typeof user.id === "string" || typeof user.username === "string");
  if (isValidUser && persistHint && typeof user?.id === "string") setAuthHint(user.id);
  useAuthStore.setState({
    isAuthenticated: isValidUser,
    isLoading: false,
    isInitialized: true,
  });
};

interface AuthState {
  // Только UI состояние - данные API хранятся в TanStack Query
  isInitialized: boolean;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;

  // UI actions
  setIsInitialized: (initialized: boolean) => void;
  setIsLoading: (loading: boolean) => void;
  setIsAuthenticated: (authenticated: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()((set) => ({
  isInitialized: false,
  isLoading: true,
  isAuthenticated: false,
  error: null,

  setIsInitialized: (initialized) => set({ isInitialized: initialized }),
  setIsLoading: (loading) => set({ isLoading: loading }),
  setIsAuthenticated: (authenticated) => set({ isAuthenticated: authenticated }),
  setError: (error) => set({ error }),
  clearError: () => set({ error: null }),

  logout: () => {
    authGeneration += 1;
    set({
      isAuthenticated: false,
      error: null,
      isLoading: false,
      isInitialized: true,
    });
    if (typeof window !== "undefined") {
      localStorage.removeItem(LOCAL_STORAGE_KEYS.AUTH_STORAGE);
      sessionStorage.removeItem("banInfo");
      clearAuthHint();
    }
  },
}));

function isOAuthCallbackPage(): boolean {
  if (typeof window === "undefined") return false;
  if (!/^\/auth\/[^/]+\/callback\/?$/.test(window.location.pathname)) return false;
  return new URLSearchParams(window.location.search).has("code");
}

const meOptions = () =>
  ({
    ...getMeQueryOptions({
      timeout: AUTH_CONSTANTS.ME_REQUEST_TIMEOUT_MS,
    }),
    retry: 0 as const,
  }) as ReturnType<typeof getMeQueryOptions> & { retry: 0 };

function applyMeSuccess(
  generation: number,
  response: { data?: { id?: string | null; username?: string | null } | null },
) {
  if (generation !== authGeneration) return;
  const user = response?.data ?? null;
  const isValidUser =
    !!user && (typeof user.id === "string" || typeof user.username === "string");
  if (isValidUser && typeof user?.id === "string") setAuthHint(user.id);
  useAuthStore.setState({
    isAuthenticated: isValidUser,
    isLoading: false,
    isInitialized: true,
  });
  if (!isValidUser) clearAuthHint();
}

/**
 * Сессию отвергает только сервер: 401/403. Обрыв сети, таймаут и 5xx ничего не
 * говорят о сессии — сбросить на них вход значит отправить пользователя на /login,
 * где без сети тоже ничего не грузится, и защищённая раскладка гоняет его по кругу
 * /profile ↔ /login, заново дёргая все запросы шапки (KAN-1282, GlitchTip 115).
 */
function applyMeFailure(generation: number, error: unknown) {
  if (generation !== authGeneration) return;
  const status = (error as { response?: { status?: unknown } } | null)?.response?.status;
  const sessionRejected = status === 401 || status === 403;
  if (sessionRejected) clearAuthHint();
  useAuthStore.setState((s) => ({
    isAuthenticated: sessionRejected ? false : s.isAuthenticated,
    isLoading: false,
    isInitialized: true,
  }));
}

export const initializeAuth = () => {
  if (typeof window !== "undefined") {
    const generation = ++authGeneration;
    if (isOAuthCallbackPage()) {
      useAuthStore.setState({
        isLoading: false,
        isInitialized: true,
        isAuthenticated: false,
      });
      return;
    }

    const queryClient = getQueryClient();
    const cached = queryClient.getQueryData(getMeQueryKey());
    const state = queryClient.getQueryState(getMeQueryKey());
    const dataAge = state?.dataUpdatedAt ? Date.now() - state.dataUpdatedAt : Infinity;

    const hint = getAuthHint();
    if (hint) {
      useAuthStore.setState({
        isAuthenticated: true,
        isLoading: false,
        isInitialized: true,
      });
      queryClient
        .fetchQuery(meOptions())
        .then((response) => applyMeSuccess(generation, response))
        .catch((error: unknown) => applyMeFailure(generation, error));
      return;
    }

    if (cached && dataAge < FRESH_MS) {
      setAuthFromCache(cached, true);
      return;
    }

    if (cached) {
      setAuthFromCache(cached, true);
      queryClient
        .fetchQuery(meOptions())
        .then((response) => applyMeSuccess(generation, response))
        .catch((error: unknown) => applyMeFailure(generation, error));
      return;
    }

    useAuthStore.setState({
      isInitialized: false,
      isLoading: true,
      isAuthenticated: false,
    });
    queryClient
      .fetchQuery(meOptions())
      .then((response) => applyMeSuccess(generation, response))
      .catch((error: unknown) => applyMeFailure(generation, error));
  }
};
